import { useEffect, useState } from 'react';
import {
  makeStyles, tokens, Title1, Title3, Textarea, Button, Spinner, Text, Badge,
} from '@fluentui/react-components';
import { apiGet, apiPost, apiUpload } from '../api/client';
import type { ChatQueryResponse, DocumentSummary, DocumentStatus, UploadDocumentResponse } from '../api/types';

const useStyles = makeStyles({
  wrap: { display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px' },
  section: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '16px 20px',
    marginBottom: '16px',
  },
  answerBox: { whiteSpace: 'pre-wrap', fontSize: '15px', lineHeight: 1.55 },
  citation: {
    display: 'block',
    marginTop: '8px',
    padding: '8px 12px',
    background: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
    borderRadius: '4px',
    fontSize: '13px',
  },
  chunk: {
    display: 'block',
    marginTop: '8px',
    padding: '8px 12px',
    background: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
  },
  scoresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '8px' },
  scoreRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px' },
  err: { color: tokens.colorPaletteRedForeground1 },
  uploadRow: { display: 'flex', gap: '8px', alignItems: 'center' },
});

function scoreColor(v: number): 'success' | 'warning' | 'danger' {
  if (v >= 0.7) return 'success';
  if (v >= 0.4) return 'warning';
  return 'danger';
}

function statusLabel(s: DocumentStatus): string {
  return ['Uploaded', 'Processing', 'Ready', 'Failed'][s] ?? String(s);
}
function statusColor(s: DocumentStatus): 'informative' | 'brand' | 'success' | 'danger' {
  return (['informative', 'brand', 'success', 'danger'] as const)[s] ?? 'informative';
}

export default function Chat() {
  const s = useStyles();
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ChatQueryResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Track the currently-processing upload so we can show live progress.
  const [pendingDoc, setPendingDoc] = useState<DocumentSummary | null>(null);
  const [uploadErr, setUploadErr] = useState<string | null>(null);

  // Ready-docs counter — surfaces whether there's anything to retrieve.
  const [readyCount, setReadyCount] = useState<number | null>(null);

  // Refresh the ready-count on mount and whenever a doc finishes processing.
  async function refreshDocs() {
    try {
      const list = await apiGet<DocumentSummary[]>('/api/documents');
      setReadyCount(list.filter((d) => d.status === 2).length);
    } catch { /* silent — the count is decorative */ }
  }
  useEffect(() => { refreshDocs(); }, []);

  // Poll the most recently uploaded doc every 1.5s until it terminates.
  useEffect(() => {
    if (!pendingDoc) return;
    if (pendingDoc.status === 2 || pendingDoc.status === 3) return;

    let cancelled = false;
    const t = setInterval(async () => {
      try {
        const doc = await apiGet<DocumentSummary>(`/api/documents/${pendingDoc.id}`);
        if (cancelled) return;
        setPendingDoc(doc);
        if (doc.status === 2 || doc.status === 3) {
          clearInterval(t);
          refreshDocs();  // bump the corpus-ready badge
        }
      } catch { /* keep polling — transient errors are fine */ }
    }, 1500);
    return () => { cancelled = true; clearInterval(t); };
  }, [pendingDoc]);

  async function ask() {
    setErr(null); setBusy(true);
    try {
      const r = await apiPost<ChatQueryResponse>('/api/chat/query', { question, topK: 4 });
      setResult(r);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr(null);
    // Optimistic placeholder so the status card appears immediately.
    setPendingDoc({
      id: 'pending', fileName: file.name, sizeBytes: file.size,
      pageCount: 0, chunkCount: 0, status: 0,
      errorMessage: null, uploadedAt: new Date().toISOString(), processedAt: null,
    });
    try {
      const r = await apiUpload<UploadDocumentResponse>('/api/documents/upload', file);
      // Seed with the real ID so polling can hit /api/documents/{id}.
      setPendingDoc((prev) => prev
        ? { ...prev, id: r.documentId, status: r.status }
        : null);
    } catch (err) {
      setUploadErr(`Upload failed: ${(err as Error).message}`);
      setPendingDoc(null);
    }
  }

  const noReadyDocs = readyCount === 0;
  const canAsk = !busy && question.trim().length > 0;

  return (
    <div>
      <Title1 as="h1" style={{ marginBottom: 16 }}>Chat — Healthcare Documentation Assistant</Title1>

      <div className={s.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Title3 as="h2">Upload a healthcare document</Title3>
          {readyCount !== null && (
            <Badge color={readyCount > 0 ? 'success' : 'informative'} appearance="tint">
              {readyCount} doc{readyCount === 1 ? '' : 's'} ready to query
            </Badge>
          )}
        </div>
        <Text size={200} block>PDF only. Chunking + embedding runs in the background.</Text>
        <div className={s.uploadRow} style={{ marginTop: 8 }}>
          <input type="file" accept="application/pdf,.pdf" onChange={onFile} />
        </div>
        {uploadErr && <Text style={{ color: tokens.colorPaletteRedForeground1, marginTop: 8, display: 'block' }}>{uploadErr}</Text>}

        {pendingDoc && (
          <div style={{
            marginTop: 12,
            padding: '10px 12px',
            background: tokens.colorNeutralBackground2,
            border: `1px solid ${tokens.colorNeutralStroke2}`,
            borderRadius: 6,
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}>
            {(pendingDoc.status === 0 || pendingDoc.status === 1) && <Spinner size="tiny" />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pendingDoc.fileName}
              </div>
              <div style={{ fontSize: 12, color: tokens.colorNeutralForeground3 }}>
                {pendingDoc.status === 0 && 'Uploading to blob storage…'}
                {pendingDoc.status === 1 && 'Extracting text, chunking, and generating embeddings…'}
                {pendingDoc.status === 2 && `Ready — ${pendingDoc.pageCount} page${pendingDoc.pageCount === 1 ? '' : 's'}, ${pendingDoc.chunkCount} chunk${pendingDoc.chunkCount === 1 ? '' : 's'} indexed.`}
                {pendingDoc.status === 3 && (pendingDoc.errorMessage || 'Processing failed.')}
              </div>
            </div>
            <Badge color={statusColor(pendingDoc.status)}>{statusLabel(pendingDoc.status)}</Badge>
          </div>
        )}
      </div>

      <div className={s.wrap}>
        <div>
          <div className={s.section}>
            <Title3 as="h2">Your question</Title3>
            <Textarea
              value={question}
              onChange={(_, d) => setQuestion(d.value)}
              placeholder="Ask something the uploaded documents can answer…"
              rows={3}
              style={{ width: '100%', marginTop: 8 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
              <Button appearance="primary" onClick={ask} disabled={!canAsk}>
                {busy ? 'Asking…' : 'Ask'}
              </Button>
              {busy && <Spinner size="tiny" />}
              {noReadyDocs && !busy && (
                <Text size={200} style={{ color: tokens.colorPaletteDarkOrangeForeground1 }}>
                  No documents ready — upload a PDF first, or the assistant will refuse.
                </Text>
              )}
            </div>
            {err && <Text className={s.err} style={{ marginTop: 8, display: 'block' }}>{err}</Text>}
          </div>

          {result && (
            <>
              <div className={s.section}>
                <Title3 as="h2">Answer</Title3>
                <div className={s.answerBox}>{result.answer}</div>

                <Title3 as="h3" style={{ marginTop: 16 }}>Citations</Title3>
                {result.citations.length === 0 && <Text size={200}>No citations detected.</Text>}
                {result.citations.map((c) => (
                  <div key={c.chunkId} className={s.citation}>
                    <strong>{c.marker}</strong> {c.documentName} (page {c.pageNumber})
                    <div style={{ marginTop: 4, color: tokens.colorNeutralForeground2 }}>{c.snippet}</div>
                  </div>
                ))}
              </div>

              <div className={s.section}>
                <Title3 as="h2">Retrieved chunks ({result.retrievedChunks.length})</Title3>
                {result.retrievedChunks.map((r) => (
                  <div key={r.chunkId} className={s.chunk}>
                    <strong>{r.documentName}</strong> — page {r.pageNumber} — score {r.score.toFixed(3)}
                    <div style={{ marginTop: 4 }}>{r.content.slice(0, 240)}{r.content.length > 240 ? '…' : ''}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div>
          {result && (
            <>
              <div className={s.section}>
                <Title3 as="h2">Evaluation</Title3>
                <div className={s.scoresGrid}>
                  <div className={s.scoreRow}>
                    <span>Groundedness</span>
                    <Badge color={scoreColor(result.evaluation.groundedness)}>{result.evaluation.groundedness.toFixed(2)}</Badge>
                  </div>
                  <div className={s.scoreRow}>
                    <span>Relevance</span>
                    <Badge color={scoreColor(result.evaluation.relevance)}>{result.evaluation.relevance.toFixed(2)}</Badge>
                  </div>
                  <div className={s.scoreRow}>
                    <span>Citation</span>
                    <Badge color={scoreColor(result.evaluation.citation)}>{result.evaluation.citation.toFixed(2)}</Badge>
                  </div>
                  <div className={s.scoreRow}>
                    <span>Safety</span>
                    <Badge color={scoreColor(result.evaluation.safety)}>{result.evaluation.safety.toFixed(2)}</Badge>
                  </div>
                  <div className={s.scoreRow} style={{ gridColumn: 'span 2', marginTop: 8, fontWeight: 600 }}>
                    <span>Aggregate</span>
                    <Badge color={scoreColor(result.evaluation.aggregate)}>{result.evaluation.aggregate.toFixed(2)}</Badge>
                  </div>
                </div>
                <div style={{ marginTop: 12, fontSize: 12, color: tokens.colorNeutralForeground3, lineHeight: 1.5 }}>
                  <div><strong>Grounded:</strong> {result.evaluation.groundednessReason}</div>
                  <div><strong>Relevance:</strong> {result.evaluation.relevanceReason}</div>
                  <div><strong>Citations:</strong> {result.evaluation.citationReason}</div>
                  <div><strong>Safety:</strong> {result.evaluation.safetyReason}</div>
                </div>
              </div>

              <div className={s.section}>
                <Title3 as="h2">Tokens & cost</Title3>
                <div className={s.scoreRow}><span>Model</span><span>{result.usage.modelName}</span></div>
                <div className={s.scoreRow}><span>Input tokens</span><span>{result.usage.inputTokens.toLocaleString()}</span></div>
                <div className={s.scoreRow}><span>Output tokens</span><span>{result.usage.outputTokens.toLocaleString()}</span></div>
                <div className={s.scoreRow}><span>Latency</span><span>{result.usage.latencyMs.toFixed(0)} ms</span></div>
                <div className={s.scoreRow}><span>Est. cost</span><span>${result.usage.estimatedCostUsd.toFixed(6)}</span></div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
